// ─────────────────────────────────────────────────────────────────────────────
// ADRESSES D'UN DOSSIER — ADR-003 : `DOSSIER → 1..n ADRESSES → 0..n VISITES`
//
// 🔑 CET ÉCRAN FONCTIONNE AVANT LA MIGRATION. La route rend l'adresse de
//    `dossier.logement` comme principale DÉRIVÉE tant que la collection est vide
//    (temps 1 de l'ADR-003) : ⛔ aucune fenêtre où l'utilisateur perd son adresse.
//    Le badge « depuis le logement » le DIT — ⛔ on ne fait pas passer une dérivée
//    pour une saisie.
// ⚠️ Ajouter une adresse matérialise d'abord la dérivée, côté serveur : la 1re
//    ⛔ ne DISPARAÎT donc pas de l'écran quand on ajoute la 2e.
// ⚠️ L'adresse PRINCIPALE est miroirée dans `logement` par le serveur (temps 2) :
//    le DAL, le CERFA et les grilles ⛔ ne bougent pas.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import { MapPin, Plus, Loader2, Star } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { BlockCard } from "../../../components/BlockCard";
import { API, getAuthHeaders } from "./helpers";
import { toast } from "sonner";

const CHAMPS_VIDES = { libelle: "", adresse: "", complement: "", code_postal: "", ville: "" };

export function AdressesBlock({ dossierId, tenantId, verrouille, onUpdate }) {
  const [adresses, setAdresses] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [ouvertAjout, setOuvertAjout] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [form, setForm] = useState(CHAMPS_VIDES);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const r = await fetch(`${API}/api/metier/dossiers/${dossierId}/adresses`,
                            { headers: getAuthHeaders(tenantId), credentials: "include" });
      if (!r.ok) throw new Error(`Erreur ${r.status}`);
      const d = await r.json();
      setAdresses(d.adresses || []);
    } catch (e) { setErreur(e.message); setAdresses([]); }
  }, [dossierId, tenantId]);

  useEffect(() => { charger(); }, [charger]);

  const ajouter = async () => {
    if (!form.adresse.trim()) { toast.error("L'adresse est obligatoire."); return; }
    setEnvoi(true);
    try {
      const r = await fetch(`${API}/api/metier/dossiers/${dossierId}/adresses`, {
        method: "POST",
        headers: getAuthHeaders(tenantId, "application/json"),
        credentials: "include",
        body: JSON.stringify({ ...form, principale: false }),
      });
      if (!r.ok) {
        const c = await r.json().catch(() => ({}));
        throw new Error(c.detail || `Erreur ${r.status}`);
      }
      setForm(CHAMPS_VIDES);
      setOuvertAjout(false);
      await charger();
      onUpdate?.();
      toast.success("Adresse ajoutée.");
    } catch (e) { toast.error(e.message); }
    finally { setEnvoi(false); }
  };

  const nb = adresses?.length ?? 0;
  return (
    <div data-subnav-id="adresses">
      <BlockCard
        collapsible open testId="bloc-adresses"
        title={<span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Adresses</span>}
        action={!verrouille && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                  style={{ color: "var(--oh-accent-text)" }}
                  data-testid="adresse-ajouter-ouvrir"
                  onClick={() => setOuvertAjout(o => !o)}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une adresse
          </Button>
        )}>
        {adresses === null && (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--oh-text-muted)" }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        )}
        {erreur && (
          <div className="text-sm" data-testid="adresses-erreur"
               style={{ color: "var(--oh-stat-red-text)" }}>{erreur}</div>
        )}
        {adresses !== null && nb === 0 && !erreur && (
          <div className="text-sm" style={{ color: "var(--oh-text-muted)" }}>
            Aucune adresse renseignée.
          </div>
        )}

        {nb > 0 && (
          <ul className="space-y-2" data-testid="adresses-liste">
            {adresses.map((a) => (
              <li key={a.id} className="rounded-lg px-3 py-2 text-sm"
                  data-testid={`adresse-${a.id}`}
                  style={{ background: "var(--oh-bg-surface)",
                           border: "0.5px solid var(--oh-border)" }}>
                <div className="flex items-center gap-2 flex-wrap">
                  {a.principale && (
                    <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded"
                          style={{ background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)" }}>
                      <Star className="w-3 h-3" /> Principale
                    </span>
                  )}
                  <span style={{ color: "var(--oh-text)" }}>{a.adresse}</span>
                  {(a.code_postal || a.ville) && (
                    <span style={{ color: "var(--oh-text-secondary)" }}>
                      · {[a.code_postal, a.ville].filter(Boolean).join(" ")}
                    </span>
                  )}
                  {a.libelle && (
                    <span style={{ color: "var(--oh-text-muted)" }}>· {a.libelle}</span>
                  )}
                  {a.derivee && (
                    // ⚠️ DIT que la donnée vient de `logement` et n'est pas encore un objet.
                    <span className="text-xs px-1.5 py-0.5 rounded"
                          data-testid="adresse-derivee"
                          style={{ background: "var(--oh-stat-amber-bg)",
                                   color: "var(--oh-stat-amber-text)" }}>
                      depuis le logement
                    </span>
                  )}
                </div>
                {a.complement && (
                  <div className="text-xs mt-0.5" style={{ color: "var(--oh-text-muted)" }}>
                    {a.complement}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {ouvertAjout && !verrouille && (
          <div className="mt-3 space-y-2" data-testid="adresse-formulaire">
            {[["adresse", "Adresse *"], ["complement", "Complément"],
              ["code_postal", "Code postal"], ["ville", "Ville"],
              ["libelle", "Libellé (ex. Bâtiment B)"]].map(([cle, label]) => (
              <label key={cle} className="block">
                <span className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>{label}</span>
                <input
                  value={form[cle]}
                  onChange={(e) => setForm(f => ({ ...f, [cle]: e.target.value }))}
                  data-testid={`adresse-champ-${cle}`}
                  placeholder={label.replace(" *", "")}
                  className="w-full rounded-lg px-3 text-sm"
                  style={{ minHeight: 36, background: "var(--oh-bg-surface)",
                           border: "0.5px solid var(--oh-border)", color: "var(--oh-text)" }} />
              </label>
            ))}
            <div className="flex gap-2">
              <Button size="sm" onClick={ajouter} disabled={envoi}
                      data-testid="adresse-enregistrer">
                {envoi ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Enregistrer l'adresse
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setOuvertAjout(false); setForm(CHAMPS_VIDES); }}>
                Annuler
              </Button>
            </div>
          </div>
        )}
      </BlockCard>
    </div>
  );
}

export default AdressesBlock;
